import { useEffect, useState } from "react";
import profilePhoto from "../images/profile.jpg";
import convuLogo from "../images/convu-logo.png";

/* ----------------------------------------------------------- links */

const LINKS = {
  convuProfile: "https://convu.ai/team/convu/virensamani",
  convu: "https://www.convu.ai",
  linkedin: "https://www.linkedin.com/in/viren-samani/",
  email: "mailto:info@convu.ai",
  cal: "https://cal.com/convu/viren",
};

/* ----------------------------------------------------------- icons */

const LinkedInIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-1 1.83-2.06 3.77-2.06 4.03 0 4.78 2.65 4.78 6.1V21h-4v-5.4c0-1.29-.02-2.95-1.8-2.95-1.8 0-2.08 1.4-2.08 2.85V21h-4V9Z" />
  </svg>
);

const ConvuLogo = (): JSX.Element => (
  <img className="brand-logo" src={convuLogo} alt="Convu" />
);

const MailIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
    <path d="m3 6 9 7 9-7" />
  </svg>
);

const SunIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

const MoonIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
  </svg>
);

const CalIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
    <path d="M3 9h18M8 2.5v4M16 2.5v4" />
  </svg>
);

/* ----------------------------------------------------------- theme */

const useTheme = (): [string, () => void] => {
  const [theme, setTheme] = useState<string>(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("theme") : null;
    if (saved) return saved;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  return [theme, () => setTheme((t) => (t === "dark" ? "light" : "dark"))];
};

/* ------------------------------------------------------------ page */

const Home = (): JSX.Element => {
  const [theme, toggleTheme] = useTheme();

  return (
    <>
      {/* ---- nav ---- */}
      <nav className="nav">
        <div className="shell nav-inner">
          <span className="nav-brand">Viren Samani<span className="dot">.</span></span>
          <div className="nav-links">
            <a className="nav-link" href="#mission">Mission</a>
            <a className="nav-link" href="#convu">Convu</a>
            <a className="nav-link" href="#beyond">Beyond work</a>
            <a className="nav-link" href="#contact">Contact</a>
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
              {theme === "dark" ? <SunIcon /> : <MoonIcon />}
            </button>
          </div>
        </div>
      </nav>

      {/* ---- hero ---- */}
      <header className="shell hero">
        <span className="status-pill">
          <span className="live" />
          Building Convu in public
        </span>
        <div>
          <img className="hero-photo" src={profilePhoto} alt="Viren Samani" />
        </div>
        <h1 className="hero-name">Viren Samani</h1>
        <p className="hero-role">
          Founder of Convu<span className="sep">/</span>
          <span className="role-alt">ex-JP Morgan</span>
        </p>
        <p className="hero-tagline">
          Algorithms move markets.{" "}
          <span className="accent">Relationships move the world.</span>
        </p>
        <div className="hero-actions">
          <a className="btn btn-primary" href={LINKS.convuProfile} target="_blank" rel="noopener noreferrer">
            Connect with me on Convu
          </a>
          <a className="btn btn-secondary" href="#mission">
            Read my story
          </a>
        </div>
        <div className="socials">
          <a className="social" href={LINKS.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" title="LinkedIn">
            <LinkedInIcon />
          </a>
          <a className="social social-logo" href={LINKS.convuProfile} target="_blank" rel="noopener noreferrer" aria-label="Convu profile" title="Convu profile">
            <ConvuLogo />
          </a>
          <a className="social" href={LINKS.email} aria-label="Email" title="Email">
            <MailIcon />
          </a>
        </div>
      </header>

      {/* ---- mission ---- */}
      <section className="section shell" id="mission">
        <span className="section-eyebrow">The mission</span>
        <h2 className="section-title">Making business human again</h2>
        <p className="section-lead">
          Why I left a finance career to bet everything on authentic connection.
        </p>
        <div className="card prose">
          <p>
            At 26, I was on the 31st floor overlooking Canary Wharf, five years into a
            finance career on the FX Sales &amp; Trading desk at JP Morgan. Surrounded
            by the brightest minds in the business, a six-figure role, a clear ladder
            to MD, and all the certainty everyone said I should want.
          </p>
          <p>
            And every night I would lie awake on the same thought. We had AI that could
            predict market crashes before they happened, yet in the lobby cafe Sarah
            the barista still asked my name every morning. We could move billions in
            microseconds, yet a brilliant engineer at a networking event could not get
            their startup across to the one investor they had chased for months. The
            most advanced technology in the world, and we were still strangers to each
            other.
          </p>
          <div className="pullquote">
            Algorithms move markets. Relationships move the world.
          </div>
          <p>
            I loved JP Morgan. The intensity, the precision, the team who always had
            your back. That floor taught me to see patterns, manage risk, and play the
            long game. But deep down I knew I was solving the wrong problems.
          </p>
          <p>
            So I did the scariest thing of my life and walked away. Not out of dislike,
            but out of hope. I traded the golden handcuffs for a laptop, a conviction,
            and a dangerously optimistic credit card limit, and went all-in on{" "}
            <strong>Convu</strong>. I wasn&apos;t running from something. I was
            running to it: a future where technology finally serves human connection
            instead of replacing it.
          </p>
          <p>
            We&apos;re living through a paradox: more &ldquo;connections&rdquo; than
            ever, less depth than ever. We&apos;ve reduced people to LinkedIn profiles
            and turned introductions into transactions, forgetting that business has
            always been fundamentally human. Convu uses AI to level the playing field,
            so authentic connection wins over pedigree and polish, because{" "}
            <strong>AI should work for humans, not replace them.</strong>
          </p>
          <p>
            Walking away was terrifying. But waking up at 80 wondering{" "}
            &ldquo;what if&rdquo; would be far scarier. This is just the start.
          </p>
        </div>

        <div className="bets">
          <div className="bet">
            <span className="arrow">&rarr;</span>
            <p>Authenticity is the new competitive advantage.</p>
          </div>
          <div className="bet">
            <span className="arrow">&rarr;</span>
            <p>Five real allies beat 5,000 passive contacts.</p>
          </div>
          <div className="bet">
            <span className="arrow">&rarr;</span>
            <p>Connection &gt; Collection. Always.</p>
          </div>
        </div>
      </section>

      {/* ---- convu ---- */}
      <section className="section shell" id="convu">
        <span className="section-eyebrow">What I&apos;m building</span>
        <h2 className="section-title">Convu</h2>
        <p className="section-lead">
          The memory layer for the relationships that matter.
        </p>

        <div className="convu">
          <span className="convu-tag">Convu</span>
          <h3>
            Your next client, investor or hire is already in your network. You just
            forgot them.
          </h3>
          <p>
            Convu makes sure you remember. Every conversation captured, remembered and
            actioned, so every handshake becomes an opportunity, from boardrooms to
            coffee shops to expo floors.
          </p>
          <a className="btn" href={LINKS.convu} target="_blank" rel="noopener noreferrer">
            Visit Convu &rarr;
          </a>
        </div>

        <div className="stats">
          <div className="stat">
            <div className="stat-num">98%</div>
            <div className="stat-label">of deals don&apos;t close on the first meeting</div>
          </div>
          <div className="stat">
            <div className="stat-num">48 hrs</div>
            <div className="stat-label">until the moment fades and the details are gone</div>
          </div>
          <div className="stat">
            <div className="stat-num">3x</div>
            <div className="stat-label">higher reply rate when the follow-up actually lands</div>
          </div>
        </div>

        <div className="steps">
          <div className="step">
            <span className="step-num">01</span>
            <h3>Connect</h3>
            <p>
              One scan of a LinkedIn QR and a quick voice note as you walk away. The
              moment is captured and enriched with who, where and when before it leaves
              your head.
            </p>
          </div>
          <div className="step">
            <span className="step-num">02</span>
            <h3>Coordinate</h3>
            <p>
              AI drafts the follow-up in your voice, referencing what you actually
              discussed. You approve it, and it sends from your own WhatsApp, LinkedIn
              or email.
            </p>
          </div>
          <div className="step">
            <span className="step-num">03</span>
            <h3>Compound</h3>
            <p>
              Every handshake becomes searchable and every voice note becomes a lead.
              Every room you enter compounds into revenue.
            </p>
          </div>
        </div>
      </section>

      {/* ---- beyond work ---- */}
      <section className="section shell" id="beyond">
        <span className="section-eyebrow">Beyond work</span>
        <h2 className="section-title">What drives me off the clock</h2>
        <p className="section-lead">
          The instincts and people that keep me grounded.
        </p>
        <div className="card prose">
          <p>
            I&apos;m terrified of mediocrity. Not failure, mediocrity. I&apos;d rather
            spectacularly fail at something ambitious than safely succeed at something
            average. That instinct is why the comfortable path at JP Morgan felt like a
            slow death, and why I left.
          </p>
          <p>
            It&apos;s also why I give my time to causes that change how people see
            themselves: <strong>Soul Support</strong>, working with the homeless, and{" "}
            <strong>Bhakti Initiatives</strong>, which gets young people into
            volunteering. There&apos;s something powerful that happens when someone
            realises they&apos;re capable of more than they thought. I&apos;m
            competitive by nature (Monopoly brings out my worst), but that energy is
            best spent on things that actually matter.
          </p>
        </div>

        <div className="facts">
          <div className="fact">
            <div className="fact-emoji">🤔 🤣 📈</div>
            <div className="fact-label">Me, in three emojis</div>
            <div className="fact-value">Most me &middot; Most used &middot; Current mood</div>
          </div>
          <div className="fact">
            <div className="fact-emoji">📖</div>
            <div className="fact-label">On the shelf</div>
            <div className="fact-value">How to Win Friends and Influence People</div>
          </div>
          <div className="fact">
            <div className="fact-emoji">🏏</div>
            <div className="fact-label">Sports</div>
            <div className="chips">
              <span className="chip">Cricket</span>
              <span className="chip">Swimming</span>
            </div>
          </div>
          <div className="fact">
            <div className="fact-emoji">🍝</div>
            <div className="fact-label">Cuisines</div>
            <div className="chips">
              <span className="chip">Italian</span>
              <span className="chip">Indian</span>
              <span className="chip">Mexican</span>
            </div>
          </div>
          <div className="fact">
            <div className="fact-emoji">✈️</div>
            <div className="fact-label">Dream destinations</div>
            <div className="chips">
              <span className="chip">Portugal</span>
              <span className="chip">Spain</span>
            </div>
          </div>
          <div className="fact">
            <div className="fact-emoji">☕</div>
            <div className="fact-label">How I like to connect</div>
            <div className="fact-value">Coffee, a game of pool, or Convu Spark</div>
          </div>
        </div>
      </section>

      {/* ---- get in touch ---- */}
      <section className="section shell" id="contact">
        <span className="section-eyebrow">Get in touch</span>
        <h2 className="section-title">Let&apos;s build something human</h2>
        <p className="section-lead">
          Looking to partner, collaborate, or just compare notes? Reach me through any
          of these.
        </p>
        <div className="contact-grid">
          <a className="contact-card" href={LINKS.convuProfile} target="_blank" rel="noopener noreferrer">
            <span className="contact-icon contact-icon-logo"><ConvuLogo /></span>
            <span className="contact-label">Convu</span>
            <span className="contact-value">Connect with me directly</span>
          </a>
          <a className="contact-card" href={LINKS.cal} target="_blank" rel="noopener noreferrer">
            <span className="contact-icon"><CalIcon /></span>
            <span className="contact-label">Book a chat</span>
            <span className="contact-value">Grab a 15-min catch-up</span>
          </a>
          <a className="contact-card" href={LINKS.email}>
            <span className="contact-icon"><MailIcon /></span>
            <span className="contact-label">Email</span>
            <span className="contact-value">info@convu.ai</span>
          </a>
          <a className="contact-card" href={LINKS.linkedin} target="_blank" rel="noopener noreferrer">
            <span className="contact-icon"><LinkedInIcon /></span>
            <span className="contact-label">LinkedIn</span>
            <span className="contact-value">Follow the journey</span>
          </a>
        </div>
      </section>

      {/* ---- footer ---- */}
      <footer className="footer">
        <div className="shell">
          <p className="footer-cta">Let&apos;s build something human.</p>
          <div className="footer-links">
            <a href={LINKS.convuProfile} target="_blank" rel="noopener noreferrer">Convu profile</a>
            <a href={LINKS.convu} target="_blank" rel="noopener noreferrer">Convu</a>
            <a href={LINKS.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>
            <a href={LINKS.cal} target="_blank" rel="noopener noreferrer">Book a chat</a>
            <a href={LINKS.email}>Email</a>
          </div>
          <p className="footer-note">
            &copy; {new Date().getFullYear()} Viren Samani &middot; Founder of Convu
          </p>
        </div>
      </footer>
    </>
  );
};

export default Home;
